import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import JobsPage from './pages/JobsPage';
import TemplatesPage from './pages/TemplatesPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPage from './pages/AdminPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { getCurrentUser, logout } from './services/auth';
import { Button } from './components/ui/button';
import UserMenu from './components/UserMenu';
import { jwtDecode } from 'jwt-decode';
import './index.css';

interface DecodedToken {
  sub: string;
  is_admin: boolean;
  exp: number;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getCurrentUser());
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    setIsAuthenticated(false);
    setIsAdmin(false);
    setUserEmail(null);
    // No need for navigate here as the component will re-render and redirect
  };

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      try {
        const decodedToken: DecodedToken = jwtDecode(user.access_token);
        const now = Date.now();
        const expMs = decodedToken.exp * 1000;
        if (isNaN(expMs) || expMs <= now) {
          // Token expired or invalid - clear session and redirect to login
          logout();
          setIsAuthenticated(false);
          setIsAdmin(false);
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return;
        }
        setIsAuthenticated(true);
        setIsAdmin(!!decodedToken.is_admin);
        setUserEmail(decodedToken.sub || null);
      } catch (error) {
        console.error('Invalid token:', error);
        logout();
        setIsAuthenticated(false);
        setIsAdmin(false);
        setUserEmail(null);
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    } else {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUserEmail(null);
    }
  }, [isAuthenticated]);


  return (
    <Router>
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-4">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center space-x-8">
                <Link to="/" className="flex items-center gap-2 text-base font-semibold text-foreground hover:opacity-90" aria-label="EchoNotes home">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-black text-white dark:bg-white dark:text-black">E</span>
                  <span>EchoNotes</span>
                </Link>
                {isAuthenticated && (
                  <div className="hidden md:flex space-x-6">
                    <Link
                      to="/"
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Upload
                    </Link>
                    <Link
                      to="/jobs"
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Jobs
                    </Link>
                    <Link
                      to="/templates"
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Templates
                    </Link>
                  </div>
                )}
              </div>
              {isAuthenticated && (
                <UserMenu email={userEmail || ''} isAdmin={isAdmin} onLogout={handleLogout} />
              )}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset" element={<ResetPasswordPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
